// client/src/components/pages/LexiStar/Reports/ParameterForm.tsx
//
// A report's parameter form, generated from `report_definitions.parameters`
// (JSON Schema 2020-12, seeded by 028; ST-00188 M4-R2). from/to date pairs
// become one period on the shared UnifiedDateRangePicker — or, with
// `allowRelative` (schedules, and period reports generally), a relative period
// the provider resolves at run time (`parameters.period`: yesterday,
// last_7_days, last_month …; M4-R1 providers/shared.ts). A single `date` gets
// the same choice. Enums are ThemedSelects, string arrays with an enum are
// checkboxes and without one a comma list, booleans checkboxes, numbers
// numeric inputs, ids plain text.

import UnifiedDatePicker from '../../../common/UnifiedDatePicker';
import { ThemedSelect, UnifiedDateRangePicker } from '../../../../types/commonComponents';
import { toApiDateString } from '../../../../utils/dateUtils';
import { RELATIVE_PERIODS, type FormField } from './reportLogic';

export const inputClass =
  'w-full px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm bg-surface';

const dayToDate = (s: unknown): Date | null =>
  typeof s === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s) ? new Date(`${s}T12:00:00`) : null;

const yesterday = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return toApiDateString(d) as string;
};

/** Relative-period keywords a single day may take. */
const DAY_RELATIVE = RELATIVE_PERIODS.filter((p) => p.value === 'yesterday' || p.value === 'today');

export default function ParameterForm({
  fields,
  value,
  onChange,
  idPrefix = 'param',
  allowRelative = false,
  disabled = false,
  hide = [],
}: {
  fields: FormField[];
  value: Record<string, unknown>;
  onChange: (next: Record<string, unknown>) => void;
  idPrefix?: string;
  allowRelative?: boolean;
  disabled?: boolean;
  /** Parameter names another control owns (e.g. `scope` on a schedule's recipients). */
  hide?: string[];
}) {
  const set = (patch: Record<string, unknown>) => {
    const next = { ...value, ...patch };
    Object.keys(next).forEach((k) => {
      if (next[k] === undefined) delete next[k];
    });
    onChange(next);
  };
  const shown = fields.filter((f) => !f.names.some((n) => hide.includes(n)));
  if (!shown.length) {
    return <p className="text-sm text-slate-500">This report takes no parameters.</p>;
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
      {shown.map((f) => {
        const id = `${idPrefix}-${f.name}`;
        const label = (
          <label htmlFor={id} className="block text-xs font-semibold text-slate-600 mb-1">
            {f.label}
            {f.required && <span className="text-slate-400"> *</span>}
          </label>
        );
        const help = f.description ? <p className="mt-1 text-[11px] text-slate-500">{f.description}</p> : null;
        const v = value[f.name];

        if (f.kind === 'daterange') {
          const [fromKey, toKey] = f.names as [string, string];
          const relative = typeof value.period === 'string' ? (value.period as string) : '';
          return (
            <div key={f.name} className="sm:col-span-2">
              <span id={id} className="block text-xs font-semibold text-slate-600 mb-1">
                Period{f.required && <span className="text-slate-400"> *</span>}
              </span>
              <div className="flex flex-wrap items-center gap-2" role="group" aria-labelledby={id}>
                {allowRelative && (
                  <div className="w-56">
                    <ThemedSelect
                      aria-label="Period kind"
                      value={relative}
                      disabled={disabled}
                      onChange={(e) => {
                        const p = String(e.target.value);
                        if (p) set({ period: p, [fromKey]: undefined, [toKey]: undefined });
                        else set({ period: undefined, [fromKey]: yesterday(), [toKey]: yesterday() });
                      }}
                      className={inputClass}
                    >
                      <option value="">Fixed dates</option>
                      {RELATIVE_PERIODS.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </ThemedSelect>
                  </div>
                )}
                {!relative && (
                  // Full width on a phone (ST-00204); its own size from sm, as before.
                  <div className="w-full sm:w-auto">
                    <UnifiedDateRangePicker
                      startDate={dayToDate(value[fromKey])}
                      endDate={dayToDate(value[toKey])}
                      onStartDateChange={(d) => set({ [fromKey]: toApiDateString(d), period: undefined })}
                      onEndDateChange={(d) => set({ [toKey]: toApiDateString(d), period: undefined })}
                      disabled={disabled}
                    />
                  </div>
                )}
              </div>
              {help}
            </div>
          );
        }

        if (f.kind === 'date' || f.kind === 'datetime') {
          const relative = typeof value.period === 'string' && !value[f.name] ? (value.period as string) : '';
          return (
            <div key={f.name}>
              {label}
              <div className="flex flex-wrap items-center gap-2">
                {allowRelative && (
                  <div className="w-40">
                    <ThemedSelect
                      aria-label={`${f.label} kind`}
                      value={relative}
                      disabled={disabled}
                      onChange={(e) => {
                        const p = String(e.target.value);
                        if (p) set({ period: p, [f.name]: undefined });
                        else set({ period: undefined, [f.name]: yesterday() });
                      }}
                      className={inputClass}
                    >
                      <option value="">A fixed day</option>
                      {DAY_RELATIVE.map((p) => (
                        <option key={p.value} value={p.value}>
                          {p.label}
                        </option>
                      ))}
                    </ThemedSelect>
                  </div>
                )}
                {!relative && (
                  <UnifiedDatePicker
                    id={id}
                    value={dayToDate(v)}
                    onChange={(d: Date | null) => set({ [f.name]: d ? toApiDateString(d) : undefined })}
                    dateFormat="yyyy/MM/dd"
                    className={inputClass}
                    wrapperClassName="w-40"
                    placeholder="yyyy/mm/dd"
                    sheetPlaceholder="Choose a day"
                    disabled={disabled}
                    autoComplete="off"
                    title={f.label}
                  />
                )}
              </div>
              {help}
            </div>
          );
        }

        if (f.kind === 'select') {
          return (
            <div key={f.name}>
              {label}
              <ThemedSelect
                id={id}
                aria-label={f.label}
                value={v === undefined || v === null ? '' : String(v)}
                onChange={(e) => set({ [f.name]: String(e.target.value) || undefined })}
                className={inputClass}
                disabled={disabled}
              >
                <option value="">{f.required ? 'Choose…' : 'Any'}</option>
                {f.options.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </ThemedSelect>
              {help}
            </div>
          );
        }

        if (f.kind === 'multiselect') {
          const chosen = Array.isArray(v) ? v.map(String) : [];
          return (
            <fieldset key={f.name} className="sm:col-span-2">
              <legend className="block text-xs font-semibold text-slate-600 mb-1">{f.label}</legend>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {f.options.map((o) => (
                  <label key={o.value} className="inline-flex items-center gap-1.5 text-sm text-slate-700">
                    <input
                      type="checkbox"
                      checked={chosen.includes(o.value)}
                      disabled={disabled}
                      onChange={(e) =>
                        set({
                          [f.name]: e.target.checked ? [...chosen, o.value] : chosen.filter((x) => x !== o.value),
                        })
                      }
                    />
                    {o.label}
                  </label>
                ))}
              </div>
              {help}
            </fieldset>
          );
        }

        if (f.kind === 'boolean') {
          return (
            <div key={f.name} className="flex items-end">
              <label htmlFor={id} className="inline-flex items-center gap-2 text-sm text-slate-700 py-2">
                <input
                  id={id}
                  type="checkbox"
                  checked={v === true}
                  disabled={disabled}
                  onChange={(e) => set({ [f.name]: e.target.checked })}
                />
                {f.label}
              </label>
            </div>
          );
        }

        if (f.kind === 'number') {
          return (
            <div key={f.name}>
              {label}
              <input
                id={id}
                type="number"
                inputMode="decimal"
                value={v === undefined || v === null ? '' : String(v)}
                min={f.min ?? undefined}
                max={f.max ?? undefined}
                disabled={disabled}
                onChange={(e) => set({ [f.name]: e.target.value === '' ? undefined : Number(e.target.value) })}
                className={inputClass}
              />
              {help}
            </div>
          );
        }

        // text: ids, free strings, and string arrays without an enum (a comma list)
        return (
          <div key={f.name}>
            {label}
            <input
              id={id}
              type="text"
              value={Array.isArray(v) ? v.join(', ') : v === undefined || v === null ? '' : String(v)}
              disabled={disabled}
              placeholder={
                f.format === 'uuid' ? 'UUID' : f.array ? 'Comma-separated' : undefined
              }
              onChange={(e) => {
                const raw = e.target.value;
                if (f.array) {
                  const parts = raw
                    .split(',')
                    .map((x) => x.trim())
                    .filter(Boolean);
                  set({ [f.name]: parts.length ? parts : undefined });
                } else set({ [f.name]: raw.trim() === '' ? undefined : raw });
              }}
              className={`${inputClass} ${f.format === 'uuid' ? 'font-mono text-xs' : ''}`}
            />
            {help}
          </div>
        );
      })}
    </div>
  );
}
